import React, { useState, useRef, useEffect, useCallback } from 'react';
import { Dialog } from 'primereact/dialog';
import { Button } from 'primereact/button';
import { InputText } from 'primereact/inputtext';
import { InputTextarea } from 'primereact/inputtextarea';
import { Dropdown } from 'primereact/dropdown';
import { MultiSelect } from 'primereact/multiselect';
import { InputSwitch } from 'primereact/inputswitch';
import { InputNumber } from 'primereact/inputnumber';
import { Card } from 'primereact/card';
import { Toast } from 'primereact/toast';
import { FileUpload } from 'primereact/fileupload';
import { Divider } from 'primereact/divider';
import { ConfirmDialog, confirmDialog } from 'primereact/confirmdialog';
import { QuillEditor } from '../../components/QuillEditor';
import { createChecklistTemplate, updateChecklistTemplate } from '../../service/checklistService';
import { getAll as getAppointments } from '../../service/appointmentsService';
import { apiGet } from '../../service/api-service';
import { s3UploadFile, s3GetUrl } from '../../service/s3Service';
import { UserSession } from '../../models/UserSession';

interface ChecklistTemplateDialogProps {
  visible: boolean;
  onHide: () => void;
  template?: any;
  onSave: () => void;
  session?: UserSession;
  isClientContext?: boolean;
}

interface ChecklistCondition {
  condition_type: string;
  condition_value?: string[];
  operator?: string;
  threshold_value?: number;
  options_all?: boolean;
}

interface ChecklistCategory {
  id?: string;
  name?: string;
  description?: string;
  order_index: number;
  questions: ChecklistQuestion[];
}

interface ChecklistQuestion {
  id?: string;
  question_text?: string;
  question_instruction?: string;
  order_index: number;
  required: boolean;
  non_compliant_options: ChecklistNonCompliantOption[];
}

interface ChecklistNonCompliantOption {
  id?: string;
  option_text?: string;
  order_index: number;
  corrective_actions: ChecklistCorrectiveAction[];
}

interface ChecklistCorrectiveAction {
  id?: string;
  action_text?: string;
}

const frequencyOptions = [
  { label: 'Hourly', value: 'hourly' },
  { label: 'Daily', value: 'daily' },
  { label: 'Weekly', value: 'weekly' },
  { label: 'Monthly', value: 'monthly' },
  { label: 'Quarterly', value: 'quarterly' },
  { label: 'Annually', value: 'annually' },
  { label: 'Custom', value: 'custom' }
];

// Trigger type options
const triggerTypeOptions = [
  { label: 'Daily Task Plan (DTP)', value: 'dtp' },
  { label: 'Frequency & Intervals', value: 'frequency' }
];

const conditionTypeOptions = [
  { label: 'Number of Employees', value: 'employee_count' },
  { label: 'Vehicle Types', value: 'vehicle_type' },
  { label: 'Chemical Types', value: 'chemical_type' },
  { label: 'Equipment Types', value: 'equipment_type' },
  { label: 'Job Tasks', value: 'job_task' }
];

export default function ChecklistTemplateCreateDialog({ 
  visible, 
  onHide, 
  template, 
  onSave,
  session,
  isClientContext = false
}: ChecklistTemplateDialogProps) {
  const toast = useRef<Toast>(null);
  const fileUploadRef = useRef<FileUpload>(null);
  const [loading, setLoading] = useState(false);
  const [currentStep, setCurrentStep] = useState(1);
  const [iconUrl, setIconUrl] = useState<string>('');
  const [iconUploading, setIconUploading] = useState<boolean>(false);
  const MAX_ICON_BYTES = 500 * 1024; // 500 KB
  const ALLOWED_ICON_TYPES = ['image/png', 'image/jpeg', 'image/webp'];
  
  // Determine restrictions based on context - in client context, all users have restrictions
  const shouldRestrictFields = isClientContext;
  
  // Dropdown data
  const [appointments, setAppointments] = useState<any[]>([]);
  const [industries, setIndustries] = useState<{label: string; value: any}[]>([]);
  const [jobTasks, setJobTasks] = useState<{label: string; value: string}[]>([]);
  const [equipmentTypes, setEquipmentTypes] = useState<{label: string; value: string}[]>([]);
  const [vehicleTypes, setVehicleTypes] = useState<{label: string; value: string}[]>([]);
  const [chemicalTypes, setChemicalTypes] = useState<{label: string; value: string}[]>([]);

  // Form state
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    icon_s3_key: '',
    icon_url: '',
    industry_id: '',
    industry_ids: [] as string[],
    industry_all: true,
    appointment_id: '',
    // Removed appointment_all since there's no "All appointments toggle"
    approval_appointment_id: '',
    trigger_type: 'frequency', // Default to frequency & intervals
    frequency_type: 'monthly',
    frequency_interval_days: 1,
    // Removed completion_window fields as they're not used anymore
    conditional_requirement: '',
    active: true
  });

  const [conditions, setConditions] = useState<ChecklistCondition[]>([]);
  const [categories, setCategories] = useState<ChecklistCategory[]>([
    {
      name: 'General Safety Inspection',
      description: 'Basic safety inspection requirements',
      order_index: 0,
      questions: [
        {
          question_text: 'Photo Requirements',
          question_instruction: 'Take clear photos of any safety hazards or areas of concern. Include emergency exits and fire extinguishers in your documentation.',
          order_index: 0,
          required: true,
          non_compliant_options: []
        },
        {
          question_text: 'Are all emergency exits clear and unobstructed?',
          order_index: 1,
          required: true,
          non_compliant_options: [
            {
              option_text: 'Exit blocked by equipment',
              order_index: 0,
              corrective_actions: [
                { action_text: 'Remove equipment and clear exit pathway' },
                { action_text: 'Place "Keep Clear" signs at exit' }
              ]
            },
            {
              option_text: 'Exit sign not illuminated',
              order_index: 1,
              corrective_actions: [
                { action_text: 'Replace bulbs in exit sign' }
              ]
            }
          ]
        },
        {
          question_text: 'Are fire extinguishers properly mounted and accessible?',
          order_index: 2,
          required: true,
          non_compliant_options: [
            {
              option_text: 'Extinguisher not mounted',
              order_index: 0,
              corrective_actions: [
                { action_text: 'Install proper mounting bracket' }
              ]
            }
          ]
        }
      ]
    }
  ]);

  useEffect(() => {
    if (template) {
      const incomingIds: any[] = Array.isArray(template.industry_ids)
        ? template.industry_ids
        : (template.industry_id != null && template.industry_id !== '' ? [template.industry_id] : []);
      const hasAll = incomingIds.includes(1) || template.industry_id === 1;
      setFormData({
        name: template.name || '',
        description: template.description || '',
        icon_s3_key: template.icon_s3_key || '',
        icon_url: template.icon_url || '',
        industry_id: hasAll ? 1 : (template.industry_id || ''),
        industry_ids: hasAll ? [] : incomingIds,
        industry_all: hasAll || (!template.industry_id && !(Array.isArray(template.industry_ids) && template.industry_ids.length)),
        appointment_id: template.appointment_id || '',
        // Removed appointment_all since there's no "All appointments toggle"
        approval_appointment_id: template.approval_appointment_id || '',
        trigger_type: template.trigger_type || 'frequency',
        frequency_type: template.frequency_type || 'monthly',
        frequency_interval_days: template.frequency_interval_days || 1,
        // Removed completion_window fields as they're not used anymore
        conditional_requirement: template.conditional_requirement || '',
        active: template.active !== false
      });
      
      // Icon URL now set directly from template.icon_url (API provides resolved URL)
      console.log('🔍 DEBUG: Template icon data:', {
        template_icon_url: template.icon_url,
        template_icon_s3_key: template.icon_s3_key,
        current_iconUrl_state: iconUrl
      });
      
      if (template.icon_url) {
        console.log('🔍 DEBUG: Setting iconUrl from template.icon_url:', template.icon_url);
        setIconUrl(template.icon_url);
      } else if (template.icon_s3_key) {
        console.log('🔍 DEBUG: No icon_url but found icon_s3_key, resolving URL:', template.icon_s3_key);
        // If we have an S3 key but no resolved URL, get the URL
        s3GetUrl(template.icon_s3_key).then(url => {
          console.log('🔍 DEBUG: Resolved S3 URL:', url);
          setIconUrl(url);
        }).catch(error => {
          console.error('🔍 DEBUG: Failed to resolve S3 URL:', error);
          setIconUrl('');
        });
      } else {
        console.log('🔍 DEBUG: No template icon_url or icon_s3_key, clearing iconUrl');
        setIconUrl('');
      }
      
      // Process conditions to ensure options_all is properly set and condition_value is an array
      // Also consolidate multiple conditions of the same type into single conditions with arrays
      const rawConditions = template.conditions || [];
      
      // Group conditions by type
      const conditionGroups: { [key: string]: any[] } = {};
      rawConditions.forEach((condition: any) => {
        const type = condition.condition_type;
        if (!conditionGroups[type]) {
          conditionGroups[type] = [];
        }
        conditionGroups[type].push(condition);
      });
      
      // Process each group and consolidate values
      const processedConditions = Object.values(conditionGroups).map((conditionsOfType: any[]) => {
        if (conditionsOfType.length === 1) {
          // Single condition - process normally
          const condition = conditionsOfType[0];
          let conditionValue = [];
          let optionsAll = false;
          
          if (condition.condition_value) {
            try {
              // If it's a string, try to parse as JSON
              if (typeof condition.condition_value === 'string') {
                const parsed = JSON.parse(condition.condition_value);
                conditionValue = Array.isArray(parsed) ? parsed : [parsed];
              } else if (Array.isArray(condition.condition_value)) {
                conditionValue = condition.condition_value;
              } else {
                conditionValue = [condition.condition_value];
              }
              
              // Check if "All" is selected
              if (conditionValue.includes("1") || conditionValue.includes("all")) {
                optionsAll = true;
                conditionValue = ["all"];
              }
            } catch (error) {
              console.warn('Failed to parse condition_value:', condition.condition_value, error);
              conditionValue = Array.isArray(condition.condition_value) 
                ? condition.condition_value 
                : [condition.condition_value];
            }
          }
          
          return {
            ...condition,
            options_all: condition.options_all || optionsAll,
            condition_value: conditionValue
          };
        } else {
          // Multiple conditions of same type - consolidate them
          const firstCondition = conditionsOfType[0];
          const allValues: any[] = [];
          let hasAllOption = false;
          
          conditionsOfType.forEach((condition: any) => {
            if (condition.condition_value) {
              let values: any[] = [];
              try {
                if (typeof condition.condition_value === 'string') {
                  const parsed = JSON.parse(condition.condition_value);
                  values = Array.isArray(parsed) ? parsed : [parsed];
                } else if (Array.isArray(condition.condition_value)) {
                  values = condition.condition_value;
                } else {
                  values = [condition.condition_value];
                }
                
                // Check for "all" option
                if (values.includes("1") || values.includes("all")) {
                  hasAllOption = true;
                } else {
                  allValues.push(...values);
                }
              } catch (error) {
                console.warn('Failed to parse condition_value:', condition.condition_value, error);
                if (Array.isArray(condition.condition_value)) {
                  allValues.push(...condition.condition_value);
                } else {
                  allValues.push(condition.condition_value);
                }
              }
            }
          });
          
          // Remove duplicates
          const uniqueValues = Array.from(new Set(allValues));
          
          return {
            ...firstCondition,
            options_all: hasAllOption,
            condition_value: hasAllOption ? ["all"] : uniqueValues
          };
        }
      }).flat();
      
      console.log('🔍 DEBUG: Processed conditions after loading:', processedConditions.map((c: any) => ({
        condition_type: c.condition_type,
        condition_value: c.condition_value,
        options_all: c.options_all
      })));
      
      setConditions(processedConditions);
      
      // Process categories and preserve the required field from backend
      const processedCategories = (template.categories || []).map((cat: any) => ({
        ...cat,
        questions: (cat.questions || []).map((q: any, idx: number) => ({
          ...q,
          required: q.required !== undefined ? q.required : true, // Use backend value, default to true if not provided
          order_index: q.order_index ?? idx
        }))
      }));
      setCategories(processedCategories);
    } else {
      resetForm();
    }
  }, [template]); // eslint-disable-line react-hooks/exhaustive-deps

  // Debug icon state changes
  useEffect(() => {
    console.log('🔍 DEBUG: Icon state changed:', {
      iconUrl,
      formData_icon_url: formData.icon_url,
      formData_icon_s3_key: formData.icon_s3_key,
      template_exists: !!template,
      template_id: template?.id
    });
  }, [iconUrl, formData.icon_url, formData.icon_s3_key, template]); // eslint-disable-line react-hooks/exhaustive-deps

  const loadDropdownData = useCallback(async () => {
    try {
      const appointmentsRes = await getAppointments();
      setAppointments(appointmentsRes.content || []);
      
      // Load industries from API
      const industriesRes = await apiGet('core', 'industry');
      const industriesData = industriesRes.content || [];
      const filteredIndustries = industriesData.filter((item: any) => {
        const name = (item.name || '').trim().toLowerCase();
        return item.id !== 1 && name !== 'all';
      });
      setIndustries(filteredIndustries.map((item: any) => ({ 
        label: item.name, 
        value: item.id 
      })));
      
      // Load vehicle types from API
      const vehicleTypesRes = await apiGet('core', 'vehicle-type');
      const vehicleTypesData = vehicleTypesRes.content || [];
      const filteredVehicleTypes = vehicleTypesData.filter((item: any) => {
        const name = (item.name || '').trim().toLowerCase();
        return item.id !== 1 && name !== 'all' && name !== 'all type';
      });
      setVehicleTypes(filteredVehicleTypes.map((item: any) => ({ 
        label: item.name, 
        value: item.id 
      })));
      
      console.log('🔍 DEBUG: Loaded vehicle types:', filteredVehicleTypes.map((item: any) => ({
        label: item.name,
        value: item.id,
        valueType: typeof item.id
      })));
      
      // Load chemical types from API
      const chemicalTypesRes = await apiGet('core', 'chemical-type');
      const chemicalTypesData = chemicalTypesRes.content || [];
      const filteredChemicalTypes = chemicalTypesData.filter((item: any) => {
        const name = (item.name || '').trim().toLowerCase();
        return item.id !== 1 && name !== 'all' && name !== 'all type';
      });
      setChemicalTypes(filteredChemicalTypes.map((item: any) => ({ 
        label: item.name, 
        value: item.id 
      })));
      
      // Load equipment types from API
      const equipmentTypesRes = await apiGet('core', 'equipment-type');
      const equipmentTypesData = equipmentTypesRes.content || [];
      const filteredEquipmentTypes = equipmentTypesData.filter((item: any) => {
        const name = (item.name || '').trim().toLowerCase();
        return item.id !== 1 && name !== 'all' && name !== 'all type';
      });
      setEquipmentTypes(filteredEquipmentTypes.map((item: any) => ({ 
        label: item.name, 
        value: item.id 
      })));
      
      // Load job tasks from risk-job-task API
      const jobTasksRes = await apiGet('core', 'risk-job-task', { 
        activeClientId: session?.client_id || '' 
      });
      const jobTasksData = jobTasksRes.content || [];
      const filteredJobTasks = jobTasksData.filter((item: any) => {
        const name = (item.task_name || item.name || '').trim().toLowerCase();
        return item.id !== 1 && name !== 'all' && name !== 'all type';
      });
      setJobTasks(filteredJobTasks.map((item: any) => ({ 
        label: item.task_name || item.name, 
        value: item.id 
      })));
    } catch (error) {
      console.error('Error loading dropdown data:', error);
    }
  }, [session?.client_id]);

  useEffect(() => {
    loadDropdownData();
  }, [loadDropdownData]);

  const resetForm = () => {
    setFormData({
      name: '',
      description: '',
      icon_s3_key: '',
      icon_url: '',
  industry_id: '',
  industry_ids: [],
  industry_all: true,
      appointment_id: '',
      // Removed appointment_all since there's no "All appointments toggle"
  approval_appointment_id: '',
      trigger_type: 'frequency',
      frequency_type: 'monthly',
      frequency_interval_days: 1,
      // Removed completion_window fields as they're not used anymore
      conditional_requirement: '',
      active: true
    });
    setIconUrl('');
    setConditions([]);
    setCategories([
      {
        name: 'General Safety Inspection',
        description: 'Basic safety inspection requirements',
        order_index: 0,
        questions: []
      }
    ]);
    setCurrentStep(1);
  };

  const handleInputChange = (field: string, value: any) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleIconUpload = async (event: any) => {
    const file = event.files[0];
    if (file) {
      try {
        // Validation: type
        if (!ALLOWED_ICON_TYPES.includes(file.type)) {
          toast.current?.show({
            severity: 'warn',
            summary: 'Invalid File Type',
            detail: 'Please upload a PNG, JPEG or WEBP image.',
            life: 4000,
          });
          return;
        }
        // Validation: size
        if (file.size > MAX_ICON_BYTES) {
          toast.current?.show({
            severity: 'warn',
            summary: 'File Too Large',
            detail: 'Icon must be smaller than 500 KB. Consider compressing the image.',
            life: 5000,
          });
          return;
        }

        setIconUploading(true);
        // Generate a unique S3 key for the icon
        const timestamp = Date.now();
        const sanitizedName = file.name.replace(/[^a-zA-Z0-9._-]/g, '_');
        const s3Key = `template-icons/${timestamp}-${sanitizedName}`;

        // Upload to S3
        await s3UploadFile(s3Key, file);
        console.log('Upload complete, setting S3 key:', s3Key);

        // Persist key in form
        handleInputChange('icon_s3_key', s3Key);
        console.log('Form data updated with key:', s3Key);
        
        // Add a small delay to ensure S3 consistency before getting URL
        await new Promise(resolve => setTimeout(resolve, 1000));
        
        // Get the URL and set it directly (like EditDialog pattern)
        const iconUrlFromS3 = await s3GetUrl(s3Key);
        handleInputChange('icon_url', iconUrlFromS3);
        console.log('Icon URL set:', iconUrlFromS3);
        
        toast.current?.show({
          severity: 'success',
          summary: 'Icon Uploaded',
          detail: 'Template icon has been uploaded successfully',
          life: 3000,
        });
      } catch (error) {
        console.error('Error uploading icon:', error);
        toast.current?.show({
          severity: 'error',
          summary: 'Upload Failed',
          detail: 'Failed to upload template icon',
          life: 3000,
        });
      }
      finally {
        setIconUploading(false);
      }
    }
  };

  // Use icon_url from formData directly (like EditDialog) instead of resolving S3 keys
  useEffect(() => {
    console.log('🔍 DEBUG: FormData icon_url useEffect triggered:', {
      formData_icon_url: formData.icon_url,
      current_iconUrl: iconUrl,
      template_exists: !!template
    });
    
    if (formData.icon_url) {
      console.log('🔍 DEBUG: Setting iconUrl from formData.icon_url:', formData.icon_url);
      setIconUrl(formData.icon_url);
    } else if (!template) {
      // Only clear iconUrl if we're not editing a template
      // When editing, the template useEffect will handle the icon URL
      console.log('🔍 DEBUG: No formData.icon_url and no template, clearing iconUrl');
      setIconUrl('');
    } else {
      console.log('🔍 DEBUG: No formData.icon_url but template exists, keeping current iconUrl');
    }
  }, [formData.icon_url, template]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleIconRemove = () => {
    handleInputChange('icon_s3_key', '');
    handleInputChange('icon_url', '');
    setIconUrl(''); // Also clear the preview
    
    // Clear the FileUpload component's internal state
    if (fileUploadRef.current) {
      fileUploadRef.current.clear();
    }
  };

  // Condition management
  const addCondition = () => {
    console.log('🔍 DEBUG: addCondition called:', { 
      currentConditions: conditions.length,
      currentTypes: conditions.map(c => c.condition_type)
    });
    
    // Check if we can add more conditions
    const availableTypes = getAvailableConditionTypes();
    console.log('🔍 DEBUG: availableTypes for new condition:', availableTypes.map(t => t.value));
    
    if (availableTypes.length === 0) {
      toast.current?.show({
        severity: 'warn',
        summary: 'Cannot Add Condition',
        detail: 'No more condition types are available based on current selections',
        life: 3000,
      });
      return;
    }
    
    const newCondition = {
      condition_type: availableTypes[0].value, // Use the first available type
      condition_value: [],
      operator: 'gt',
      threshold_value: 1,
      options_all: false
    };
    
    console.log('🔍 DEBUG: adding new condition:', newCondition);
    
    setConditions([...conditions, newCondition]);
  };

  // Validation function that takes specific conditions array
  const validateConditionTypeWithConditions = (conditionType: string, conditionsArray: any[]): { valid: boolean; error?: string } => {
    const currentTypes = conditionsArray.map(c => c.condition_type);
    
    // Note: Backend now supports multiple values within a single condition type,
    // so we don't need to check for duplicate condition types anymore
    
    // Check business rules about condition mixing
    if (conditionType === 'employee_count') {
      // employee_count can be combined with any other single type
      const nonEmployeeTypes = currentTypes.filter(type => type !== 'employee_count');
      if (nonEmployeeTypes.length > 1) {
        return { valid: false, error: 'Employee count can only be combined with one other condition type' };
      }
    } else {
      // For non-employee_count types
      const hasEmployeeCount = currentTypes.includes('employee_count');
      const otherNonEmployeeTypes = currentTypes.filter(type => type !== 'employee_count');
      
      // If we have employee count, we can add one more condition type
      if (hasEmployeeCount && otherNonEmployeeTypes.length >= 1) {
        return { valid: false, error: 'You can only add employee count with one other condition type' };
      }
      
      // If we don't have employee count, we can't mix different condition types
      if (!hasEmployeeCount && otherNonEmployeeTypes.length >= 1) {
        return { valid: false, error: 'Cannot mix different condition types (except with employee count)' };
      }
    }
    
    return { valid: true };
  };

  // Get available condition types based on current conditions
  const getAvailableConditionTypes = (currentConditionType?: string, currentConditionIndex?: number) => {
    const availableOptions = conditionTypeOptions.filter(option => {
      // Always include the current condition type being edited
      if (currentConditionType && option.value === currentConditionType) {
        return true;
      }
      
      // For validation, temporarily exclude the current condition being edited
      let tempConditions = conditions;
      if (currentConditionIndex !== undefined) {
        tempConditions = conditions.filter((_, index) => index !== currentConditionIndex);
      }
      
      const validation = validateConditionTypeWithConditions(option.value, tempConditions);
      return validation.valid;
    });
    
    return availableOptions;
  };

  const updateCondition = (index: number, field: string, value: any) => {
    console.log('🔍 DEBUG: updateCondition called:', { index, field, value, currentConditions: conditions.length });
    
    const updated = [...conditions];
    
    // If changing condition type, validate it
    if (field === 'condition_type') {
      // Exclude the current condition being edited from validation
      const tempConditions = conditions.filter((_, i) => i !== index);
      const validation = validateConditionTypeWithConditions(value, tempConditions);
      if (!validation.valid) {
        toast.current?.show({
          severity: 'error',
          summary: 'Invalid Condition Type',
          detail: validation.error,
          life: 3000,
        });
        return;
      }
    }
    
    updated[index] = { ...updated[index], [field]: value };
    
    // If condition type is employee_count, automatically set operator to "gt" (greater than)
    if (field === 'condition_type' && value === 'employee_count') {
      updated[index].operator = 'gt';
    }
    
    // If changing condition type, reset the related fields
    if (field === 'condition_type') {
      // Reset options_all to false and clear condition_value when switching condition types
      updated[index].options_all = false;
      updated[index].condition_value = [];
    }
    
    // Ensure condition_value is always an array
    if (field === 'condition_value' && !Array.isArray(value)) {
      updated[index].condition_value = value ? [value] : [];
    }
    
    console.log('🔍 DEBUG: updateCondition result:', { 
      updatedCondition: updated[index], 
      allConditionTypes: updated.map(c => c.condition_type)
    });
    
    setConditions(updated);
  };

  const removeCondition = (index: number) => {
    console.log('🔍 DEBUG: removeCondition called:', { 
      index, 
      conditionBeingRemoved: conditions[index]?.condition_type,
      totalConditions: conditions.length 
    });
    
    const updated = conditions.filter((_, i) => i !== index);
    console.log('🔍 DEBUG: removeCondition result:', { 
      remainingConditions: updated.length,
      remainingTypes: updated.map(c => c.condition_type)
    });
    
    setConditions(updated);
  };

  // Category management
  const addCategory = () => {
    // Validate existing categories before adding new one
    const hasIncompleteCategory = categories.some(cat => !cat.name || !(cat.name || '').trim());
    if (hasIncompleteCategory) {
      toast.current?.show({
        severity: 'warn',
        summary: 'Validation Error',
        detail: 'Please complete existing category names before adding a new category'
      });
      return;
    }

    setCategories([...categories, {
      name: '',
      description: '',
      order_index: categories.length,
      // Seed with Photo Requirements pseudo-question always first
      questions: [
        {
          question_text: 'Photo Requirements',
          question_instruction: '',
          order_index: 0,
          required: true,
          non_compliant_options: []
        }
      ]
    }]);
  };

  const updateCategory = (index: number, field: string, value: any) => {
    const updated = [...categories];
    updated[index] = { ...updated[index], [field]: value };
    setCategories(updated);
  };

  const removeCategory = (index: number) => {
    confirmDialog({
      message: 'Are you sure you want to delete this category and all its questions?',
      header: 'Delete Category',
      icon: 'pi pi-exclamation-triangle',
      accept: () => {
        setCategories(categories.filter((_, i) => i !== index));
      }
    });
  };

  const moveCategoryUp = (index: number) => {
    if (index > 0) {
      const updated = [...categories];
      [updated[index - 1], updated[index]] = [updated[index], updated[index - 1]];
      updated[index - 1].order_index = index - 1;
      updated[index].order_index = index;
      setCategories(updated);
    }
  };

  const moveCategoryDown = (index: number) => {
    if (index < categories.length - 1) {
      const updated = [...categories];
      [updated[index], updated[index + 1]] = [updated[index + 1], updated[index]];
      updated[index].order_index = index;
      updated[index + 1].order_index = index + 1;
      setCategories(updated);
    }
  };

  // Question management
  const addQuestion = (categoryIndex: number) => {
    const category = categories[categoryIndex];
    
    // Validate existing questions before adding new one
    const hasIncompleteQuestion = category.questions.some(q => !q.question_text || !(q.question_text || '').trim());
    if (hasIncompleteQuestion) {
      toast.current?.show({
        severity: 'warn',
        summary: 'Validation Error',
        detail: 'Please complete existing question text before adding a new question'
      });
      return;
    }

    const updated = [...categories];
    updated[categoryIndex].questions.push({
      question_text: '',
      question_instruction: '',
      order_index: updated[categoryIndex].questions.length,
      required: true,
      non_compliant_options: []
    });
    setCategories(updated);
  };

  const updateQuestion = (categoryIndex: number, questionIndex: number, field: string, value: any) => {
    const updated = [...categories];
    updated[categoryIndex].questions[questionIndex] = {
      ...updated[categoryIndex].questions[questionIndex],
      [field]: value
    };
    setCategories(updated);
  };

  const removeQuestion = (categoryIndex: number, questionIndex: number) => {
    confirmDialog({
      message: 'Are you sure you want to delete this question?',
      header: 'Delete Question',
      icon: 'pi pi-exclamation-triangle',
      accept: () => {
        const updated = [...categories];
        // Prevent deleting the Photo Requirements entry (first question with fixed label)
        if (updated[categoryIndex].questions[questionIndex]?.question_text === 'Photo Requirements') {
          return;
        }
        updated[categoryIndex].questions = updated[categoryIndex].questions.filter((_, i) => i !== questionIndex);
        setCategories(updated);
      }
    });
  };

  const moveQuestionUp = (categoryIndex: number, questionIndex: number) => {
    if (questionIndex > 0) {
      const updated = [...categories];
      const questions = updated[categoryIndex].questions;
      // Do not allow moving above Photo Requirements
      if (questions[questionIndex - 1]?.question_text === 'Photo Requirements') {
        return;
      }
      [questions[questionIndex - 1], questions[questionIndex]] = [questions[questionIndex], questions[questionIndex - 1]];
      questions[questionIndex - 1].order_index = questionIndex - 1;
      questions[questionIndex].order_index = questionIndex;
      setCategories(updated);
    }
  };

  const moveQuestionDown = (categoryIndex: number, questionIndex: number) => {
    const questions = categories[categoryIndex].questions;
    if (questionIndex < questions.length - 1) {
      const updated = [...categories];
      const questionsArray = updated[categoryIndex].questions;
      [questionsArray[questionIndex], questionsArray[questionIndex + 1]] = [questionsArray[questionIndex + 1], questionsArray[questionIndex]];
      questionsArray[questionIndex].order_index = questionIndex;
      questionsArray[questionIndex + 1].order_index = questionIndex + 1;
      setCategories(updated);
    }
  };

  // Non-compliant option management
  const addNonCompliantOption = (categoryIndex: number, questionIndex: number) => {
    const updated = [...categories];
    updated[categoryIndex].questions[questionIndex].non_compliant_options.push({
      option_text: '',
      order_index: updated[categoryIndex].questions[questionIndex].non_compliant_options.length,
      corrective_actions: []
    });
    setCategories(updated);
  };

  const updateNonCompliantOption = (categoryIndex: number, questionIndex: number, optionIndex: number, field: string, value: any) => {
    const updated = [...categories];
    updated[categoryIndex].questions[questionIndex].non_compliant_options[optionIndex] = {
      ...updated[categoryIndex].questions[questionIndex].non_compliant_options[optionIndex],
      [field]: value
    };
    setCategories(updated);
  };

  const removeNonCompliantOption = (categoryIndex: number, questionIndex: number, optionIndex: number) => {
    const updated = [...categories];
    updated[categoryIndex].questions[questionIndex].non_compliant_options = 
      updated[categoryIndex].questions[questionIndex].non_compliant_options.filter((_, i) => i !== optionIndex);
    setCategories(updated);
  };

  // Corrective action management
  const addCorrectiveAction = (categoryIndex: number, questionIndex: number, optionIndex: number) => {
    const updated = [...categories];
    updated[categoryIndex].questions[questionIndex].non_compliant_options[optionIndex].corrective_actions.push({
      action_text: ''
    });
    setCategories(updated);
  };

  const updateCorrectiveAction = (categoryIndex: number, questionIndex: number, optionIndex: number, actionIndex: number, value: string) => {
    const updated = [...categories];
    updated[categoryIndex].questions[questionIndex].non_compliant_options[optionIndex].corrective_actions[actionIndex] = {
      action_text: value
    };
    setCategories(updated);
  };

  const removeCorrectiveAction = (categoryIndex: number, questionIndex: number, optionIndex: number, actionIndex: number) => {
    const updated = [...categories];
    updated[categoryIndex].questions[questionIndex].non_compliant_options[optionIndex].corrective_actions = 
      updated[categoryIndex].questions[questionIndex].non_compliant_options[optionIndex].corrective_actions.filter((_, i) => i !== actionIndex);
    setCategories(updated);
  };

  const getConditionValueOptions = (conditionType: string) => {
    switch (conditionType) {
      case 'industry':
        return industries;
      case 'job_task':
        return jobTasks;
      case 'equipment_type':
        return equipmentTypes;
      case 'vehicle_type':
        return vehicleTypes;
      case 'chemical_type':
        return chemicalTypes;
      default:
        return [];
    }
  };

  const handleSave = async () => {
    try {
      setLoading(true);

      // Validate required fields
      if (!(formData.name || '').trim()) {
        toast.current?.show({
          severity: 'error',
          summary: 'Validation Error',
          detail: 'Checklist name is required'
        });
        return;
      }

      if (!shouldRestrictFields && !formData.appointment_id) {
        toast.current?.show({
          severity: 'error',
          summary: 'Validation Error',
          detail: 'Responsible appointment is required'
        });
        return;
      }

  // Validate DTP (formerly DSTI) mode requirements
    if (formData.trigger_type === 'dsti' && conditions.length === 0) {
        toast.current?.show({
          severity: 'error',
          summary: 'Validation Error',
      detail: 'At least one conditional requirement is required when using DTP mode'
        });
        return;
      }

      // Note: Backend now handles all condition validation including business rules,
      // so we let the backend validate instead of doing frontend validation
      
      if (conditions.length > 0) {
        console.log('🔍 DEBUG: Final conditions before save:', conditions.map((c, index) => ({
          index,
          condition_type: c.condition_type,
          condition_value: c.condition_value,
          options_all: c.options_all
        })));
      }

      if (categories.length === 0 || categories.every(cat => cat.questions.length === 0)) {
        toast.current?.show({
          severity: 'error',
          summary: 'Validation Error',
          detail: 'At least one category with one question is required'
        });
        return;
      }

      // Create template data with backend-supported fields
      const templateData: any = {
        name: formData.name,
        description: formData.description,
        icon_s3_key: formData.icon_s3_key,
        icon_url: formData.icon_url, // Keep URL when icon is uploaded
        // Industry: Send array for multiple industries, with "All" as ID 1
        industry_ids: formData.industry_all ? [1] : (formData.industry_ids || []),
        industry_all: formData.industry_all, // Keep industry toggle
        // Single responsible person (no "all" toggle needed)
        appointment_id: formData.appointment_id || null,
        // New approving person field
        approval_appointment_id: formData.approval_appointment_id || null,
        // Trigger and frequency configuration
        trigger_type: formData.trigger_type,
        frequency_type: formData.frequency_type,
        frequency_interval: formData.frequency_interval_days, // Send as frequency_interval
        // Conditional requirements
        conditional_requirement: formData.conditional_requirement,
        conditions: (() => {
          // Group conditions by type and consolidate values
          const conditionGroups: { [key: string]: any } = {};
          
          conditions.forEach(condition => {
            const type = condition.condition_type;
            
            if (type === 'employee_count') {
              // Employee count is always unique
              conditionGroups[type] = condition;
            } else {
              // For other types, consolidate multiple values
              if (!conditionGroups[type]) {
                conditionGroups[type] = { ...condition };
              } else {
                // Merge condition values
                const existing = conditionGroups[type];
                
                if (Array.isArray(existing.condition_value) && Array.isArray(condition.condition_value)) {
                  // Both are arrays, merge them
                  existing.condition_value = [...existing.condition_value, ...condition.condition_value];
                } else if (Array.isArray(existing.condition_value)) {
                  // Existing is array, append new value
                  if (condition.condition_value !== null) {
                    existing.condition_value.push(condition.condition_value);
                  }
                } else if (Array.isArray(condition.condition_value)) {
                  // New is array, prepend existing value
                  existing.condition_value = existing.condition_value !== null 
                    ? [existing.condition_value, ...condition.condition_value] 
                    : condition.condition_value;
                } else {
                  // Both are single values, create array
                  existing.condition_value = [existing.condition_value, condition.condition_value].filter(v => v !== null);
                }
                
                // Remove duplicates
                if (Array.isArray(existing.condition_value)) {
                  existing.condition_value = Array.from(new Set(existing.condition_value));
                }
              }
            }
          });
          
          // Convert grouped conditions to backend format
          return Object.values(conditionGroups).map((condition: any) => {
            // Define consistent condition type for backend
            type BackendCondition = {
              condition_type: string;
              condition_value: string | number[] | number | null;
              operator: string | null;
              threshold_value: number | null;
            };

            // Format condition value based on condition type
            let conditionValue: string | number[] | number | null;
            if (condition.condition_type === 'employee_count') {
              conditionValue = null;
            } else {
              if (Array.isArray(condition.condition_value)) {
                if (condition.options_all) {
                  conditionValue = "all";
                } else {
                  conditionValue = condition.condition_value.map((v: any) => Number(v));
                }
              } else {
                conditionValue = condition.condition_value ? Number(condition.condition_value) : null;
              }
            }
            
            const cleanedCondition: BackendCondition = {
              condition_type: condition.condition_type,
              condition_value: conditionValue,
              operator: condition.condition_type === 'employee_count' ? (condition.operator || 'gt') : null,
              threshold_value: condition.condition_type === 'employee_count' ? (condition.threshold_value || 1) : null
            };
            
            console.log('🔍 DEBUG: Cleaned condition (backend format):', cleanedCondition);
            return cleanedCondition;
          });
        })(), // Include conditions array with cleaned structure
        active: formData.active,
        categories
      };

      // Set is_master_template flag based on template_type
      // When editing: Check if template.template_type === "master"
      // When creating: Default to true for root admin (non-client context), false otherwise
      if (template) {
        // Editing existing template
        if (template.template_type === 'master') {
          templateData.is_master_template = true;
        }
      } else {
        // Creating new template
        // In root admin context (not client context), new templates are master templates by default
        if (!isClientContext) {
          templateData.is_master_template = true;
        }
      }

      console.log('🔍 DEBUG: Template data being sent to API:', JSON.stringify(templateData, null, 2));

      if (template) {
        await updateChecklistTemplate(template.id, templateData);
        toast.current?.show({
          severity: 'success',
          summary: 'Success',
          detail: 'Checklist template updated successfully'
        });
      } else {
        await createChecklistTemplate(templateData);
        toast.current?.show({
          severity: 'success',
          summary: 'Success',
          detail: 'Checklist template created successfully'
        });
      }

      onSave();
      onHide();
    } catch (error: any) {
      console.error('Error saving checklist template:', error);
      
      // Extract error message from the error object
      let errorDetail = 'Failed to save checklist template';
      
      if (error?.message) {
        // Check if the error message contains specific backend messages
        if (error.message.includes('Cannot edit template that has existing submissions')) {
          // Use the exact backend message
          errorDetail = 'Cannot edit template that has existing submissions. Please create a new version instead.';
        } else if (error.message.includes('foreign key constraint') || 
                   error.message.includes('non_compliant_option')) {
          errorDetail = 'Cannot update this template because it has existing submissions. Please create a new template version instead.';
        } else if (error.message.includes('Internal server error')) {
          errorDetail = error.message;
        } else {
          // Use the backend error message directly if available
          errorDetail = error.message;
        }
      }
      
      toast.current?.show({
        severity: 'error',
        summary: 'Error Saving Template',
        detail: errorDetail,
        life: 6000
      });
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    confirmDialog({
      message: 'Are you sure you want to cancel? All changes will be lost.',
      header: 'Cancel Changes',
      icon: 'pi pi-exclamation-triangle',
      accept: () => {
        resetForm();
        onHide();
      }
    });
  };

  const nextStep = () => {
    const maxStep = shouldRestrictFields ? 4 : 5; // Skip step 3 for Client Admins editing customized templates
    if (currentStep < maxStep) {
      // For Client Admins editing customized templates, skip step 3 (conditional requirements)
      if (shouldRestrictFields && currentStep === 2) {
        setCurrentStep(4); // Jump from step 2 to step 4
      } else {
        setCurrentStep(currentStep + 1);
      }
    }
  };

  const prevStep = () => {
    if (currentStep > 1) {
      // For Client Admins editing customized templates, skip step 3 (conditional requirements)
      if (shouldRestrictFields && currentStep === 4) {
        setCurrentStep(2); // Jump from step 4 to step 2
      } else {
        setCurrentStep(currentStep - 1);
      }
    }
  };

  const renderStepIndicator = () => {
    let steps = [
      { number: 1, title: 'Basic Information' },
      { number: 2, title: 'Industry & Appointment' },
      { number: 3, title: 'Conditional Requirements' },
      { number: 4, title: 'Trigger Configuration' },
      { number: 5, title: 'Categories & Questions' }
    ];

    // Filter out step 3 for Client Admins editing customized templates
    if (shouldRestrictFields) {
      steps = steps.filter(step => step.number !== 3);
      // Renumber steps for Client Admins (4 becomes 3, 5 becomes 4)
      steps = steps.map(step => {
        if (step.number > 3) {
          return { ...step, number: step.number - 1 };
        }
        return step;
      });
    }

    return (
      <div className="flex justify-content-center mb-5 pb-4" style={{ borderBottom: '1px solid #e0e0e0' }}>
        <div className="flex align-items-center">
          {steps.map((step, index) => (
            <React.Fragment key={step.number}>
              <div 
                className={`flex align-items-center justify-content-center w-3rem h-3rem border-circle cursor-pointer ${
                  currentStep === step.number ? 'bg-primary text-white' : 
                  currentStep > step.number ? 'bg-green-500 text-white' : 
                  'bg-gray-200 text-gray-600'
                }`}
                onClick={() => {
                  // For Client Admins editing customized templates, map visual step numbers back to actual step numbers
                  const actualStepNumber = shouldRestrictFields && step.number >= 3 ? step.number + 1 : step.number;
                  setCurrentStep(actualStepNumber);
                }}
              >
                {currentStep > step.number ? <i className="pi pi-check" /> : step.number}
              </div>
              {index < steps.length - 1 && (
                <div className={`w-4rem h-2px ${currentStep > step.number ? 'bg-green-500' : 'bg-gray-300'}`} />
              )}
            </React.Fragment>
          ))}
        </div>
      </div>
    );
  };

  const renderBasicInformation = () => (
    <Card title="Basic Information" className="mb-4">
      <div className="formgrid grid">
        <div className="field col-12 md:col-8">
          <label htmlFor="checklistName" className="block mb-2 font-semibold">
            Checklist Name <span className="text-red-500">*</span>
          </label>
          <InputText
            id="checklistName"
            value={formData.name}
            onChange={(e) => handleInputChange('name', e.target.value)}
            placeholder="Enter checklist name"
            className="w-full"
            disabled={shouldRestrictFields}
          />
          {shouldRestrictFields && (
            <small className="text-gray-600">These core template fields cannot be modified in client context</small>
          )}
        </div>
        
        <div className="field col-12 md:col-4">
          <label htmlFor="checklistIcon" className="block mb-2 font-semibold">
            Checklist Icon
          </label>
          
          {/* Display current icon if available */}
          {iconUrl && (
            <div className="mb-2 flex align-items-center gap-3">
              <img
                src={iconUrl}
                alt={formData.name ? `${formData.name} icon` : 'Template Icon'}
                style={{ width: '64px', height: '64px', objectFit: 'cover', borderRadius: '8px', border: '1px solid #ddd' }}
                onLoad={() => {
                  console.log('🔍 DEBUG: Icon loaded successfully:', iconUrl);
                }}
                onError={(e) => {
                  console.error('🔍 DEBUG: Image failed to load:', iconUrl);
                  // Don't retry automatically as it can cause infinite loops
                  // The image will be available after the upload completes
                }}
              />
              {!shouldRestrictFields && (
                <Button
                  type="button"
                  icon="pi pi-times"
                  className="p-button-text p-button-danger p-button-sm"
                  style={{ 
                    minWidth: '32px', 
                    width: '32px', 
                    height: '32px',
                    padding: '4px'
                  }}
                  onClick={handleIconRemove}
                  tooltip="Remove icon"
                  tooltipOptions={{ position: 'top' }}
                />
              )}
            </div>
          )}

          <FileUpload
            ref={fileUploadRef}
            mode="basic"
            accept={ALLOWED_ICON_TYPES.join(',')}
            maxFileSize={MAX_ICON_BYTES}
            onSelect={handleIconUpload} // use onSelect because we perform a custom manual upload
            chooseLabel={iconUploading ? 'Uploading...' : (iconUrl ? 'Replace Icon' : 'Upload Icon')}
            className="w-full"
            disabled={shouldRestrictFields || iconUploading}
            customUpload // prevent PrimeReact trying to POST to a non-existent URL
            uploadHandler={() => { /* no-op: handled in onSelect */ }}
          />
          {shouldRestrictFields ? (
            <small className="text-gray-600">Template icon cannot be edited in client context</small>
          ) : (
            <small className="text-gray-600">PNG/JPEG/WEBP. Max 500 KB. Ideal 64×64 (displayed), upload up to 512×512 for future-proofing.</small>
          )}
        </div>

        <div className="field col-12">
          <QuillEditor
            label="Description"
            content={formData.description}
            setContent={(value: string) => handleInputChange('description', value)}
            heightPx={20}
            toast={toast}
            hidePlaceholder={true}
          />
        </div>
      </div>
    </Card>
  );

  const renderIndustryAppointment = () => (
    <Card title="Industry & Appointments" className="mb-4">
      <div className="formgrid grid">
        {/* Row 1: Industry full width */}
        <div className="field col-12">
          <div className="flex align-items-center justify-content-between" style={{ padding: '10px 0' }}>
            <label htmlFor="industry" className="block font-semibold">
              Industry
            </label>
            <div className="flex align-items-center gap-2">
              <label className="block font-semibold">All</label>
              <InputSwitch
                checked={formData.industry_all}
                onChange={(e) => {
                  const all = (e as any).value as boolean;
                  setFormData(prev => ({
                    ...prev,
                    industry_all: all,
                    industry_ids: all ? [] : prev.industry_ids
                  }));
                }}
              />
            </div>
          </div>
          {industries.length > 0 ? (
            <MultiSelect
              id="industry"
              value={formData.industry_ids}
              onChange={(e) => setFormData(prev => ({ ...prev, industry_ids: (e as any).value }))}
              options={industries}
              placeholder={formData.industry_all ? 'All Industries' : 'Select industries'}
              display="chip"
              className="w-full"
              disabled={formData.industry_all}
              optionLabel="label"
              optionValue="value"
              filter
              filterPlaceholder="Search"
              showClear
              showSelectAll
            />
          ) : (
            <div className="w-full p-3 border-1 surface-border border-round">
              <i className="pi pi-spin pi-spinner mr-2"></i>
              Loading industries...
            </div>
          )}
        </div>
        
  {/* Row 2: Responsible + Approve side by side */}
  <div className="field col-12 md:col-6">
          <label htmlFor="appointment" className="block mb-2 font-semibold">
            Responsible Appointment <span className="text-red-500">*</span>
          </label>
          <Dropdown
            id="appointment"
            value={formData.appointment_id}
            onChange={(e) => handleInputChange('appointment_id', e.value)}
            options={appointments
              .filter((app: any) => {
                const name = (app.name || '').trim().toLowerCase();
                const idStr = String(app.id || '').trim().toLowerCase();
                return name !== 'all' && idStr !== 'all';
              })
              .map((app: any) => ({ label: app.name, value: app.id }))}
            placeholder="Select responsible person"
            className="w-full"
            disabled={shouldRestrictFields}
            filter
            filterPlaceholder="Search"
            showClear
          />
          {shouldRestrictFields && (
            <small className="text-gray-600">Appointment cannot be changed in client context</small>
          )}
        </div>
  <div className="field col-12 md:col-6">
          <label htmlFor="approvalAppointment" className="block mb-2 font-semibold">
            Approve Checklist
          </label>
          <Dropdown
            id="approvalAppointment"
            value={formData.approval_appointment_id}
            onChange={(e) => handleInputChange('approval_appointment_id', e.value)}
            options={appointments
              .filter((app: any) => {
                const name = (app.name || '').trim().toLowerCase();
                const idStr = String(app.id || '').trim().toLowerCase();
                return name !== 'all' && idStr !== 'all';
              })
              .map((app: any) => ({ label: app.name, value: app.id }))}
            placeholder="Select approving appointment (optional)"
            className="w-full"
            filter
            filterPlaceholder="Search"
            showClear
          />
          <small className="text-gray-600">If set, this appointment must approve the checklist after completion.</small>
        </div>
      </div>
    </Card>
  );

  const renderConditionalRequirements = () => {
    // Hide conditional requirements in client context
    if (shouldRestrictFields) {
      return null;
    }
    
    return (
      <Card title="Conditional Requirements" className="mb-4">
    {formData.trigger_type === 'dsti' ? (
          <div className="p-3 bg-blue-50 border-l-4 border-blue-500 mb-4">
            <p className="text-blue-800">
      <strong>DTP Mode:</strong> These conditions are required to determine when this checklist is applicable for Daily Task Plans.
              You must define at least one condition.
            </p>
          </div>
        ) : (
          <p className="text-gray-600 mb-4">
            Define when this checklist becomes applicable based on conditions. 
            You can add employee count with one other condition type, or use only one condition type.
            <br />
            <em>Note: These conditions are optional when using Frequency & Intervals mode.</em>
          </p>
        )}
        
        {conditions.map((condition, index) => (
          <Card key={index} className="mb-3 surface-border">
            <div className="formgrid grid">
              <div className="field col-12 md:col-3">
                <label className="block mb-2 font-semibold">Condition Type</label>
                <Dropdown
                  value={condition.condition_type}
                  onChange={(e) => updateCondition(index, 'condition_type', e.value)}
                  options={getAvailableConditionTypes(condition.condition_type, index)}
                  className="w-full"
                  placeholder="Select condition type"
                />
              </div>
              
              {condition.condition_type === 'employee_count' ? (
                <>
                  <div className="field col-12 md:col-6">
                    <label className="block mb-2 font-semibold">More than (number of employees)</label>
                    <InputNumber
                      value={condition.threshold_value}
                      onValueChange={(e) => updateCondition(index, 'threshold_value', e.value)}
                      className="w-full"
                      min={0}
                      placeholder="Enter number"
                    />
                  </div>
                </>
              ) : (
                <div className="field col-12 md:col-6">
                  <div className="flex align-items-center justify-content-between mb-2" style={{ minHeight: '1.5rem' }}>
                    <label className="block font-semibold">Select Options</label>
                    <div className="flex align-items-center gap-2">
                      <label className="block font-semibold">All</label>
                      <InputSwitch
                        checked={condition.options_all || false}
                        onChange={(e) => {
                          const all = (e as any).value as boolean;
                          // Update both options_all and condition_value in a single call
                          const updated = [...conditions];
                          updated[index] = { 
                            ...updated[index], 
                            options_all: all,
                            // When "All" is selected, use ["all"] as the condition_value
                            // When "All" is deselected, clear the condition_value
                            condition_value: all ? ["all"] : []
                          };
                          setConditions(updated);
                        }}
                      />
                    </div>
                  </div>
                  <MultiSelect
                    value={(() => {
                      // When "All" is selected, MultiSelect should show empty since it's disabled
                      if (condition.options_all) {
                        return [];
                      }
                      
                      // Ensure values exist in options to prevent empty chips
                      const options = getConditionValueOptions(condition.condition_type);
                      const validValues = Array.isArray(condition.condition_value) 
                        ? condition.condition_value.filter(val => 
                            val !== "all" && options.some(opt => opt.value === val)
                          )
                        : [];
                      
                      console.log('🔍 MultiSelect value processing:', {
                        originalValue: condition.condition_value,
                        validValues,
                        options: options.map(o => ({ label: o.label, value: o.value, type: typeof o.value })),
                        optionsAll: condition.options_all
                      });
                      
                      return validValues;
                    })()}
                    onChange={(e) => {
                      console.log('🔍 MultiSelect onChange:', {
                        selectedValues: e.value,
                        availableOptions: getConditionValueOptions(condition.condition_type),
                        conditionType: condition.condition_type
                      });
                      updateCondition(index, 'condition_value', e.value);
                    }}
                    options={getConditionValueOptions(condition.condition_type)}
                    placeholder={condition.options_all ? 'All Options' : 'Select options'}
                    display="chip"
                    className="w-full"
                    optionLabel="label"
                    optionValue="value"
                    filter
                    filterPlaceholder="Search"
                    showClear
                    showSelectAll
                    disabled={condition.options_all}
                  />
                </div>
              )}
              
              <div className="field col-12 md:col-3 flex align-items-end">
                <Button
                  icon="pi pi-trash"
                  className="p-button-danger p-button-outlined"
                  onClick={() => removeCondition(index)}
                  tooltip="Remove condition"
                />
              </div>
            </div>
          </Card>
        ))}
        
        <Button
          label="Add Condition"
          icon="pi pi-plus"
          onClick={addCondition}
          className="p-button-outlined"
          disabled={getAvailableConditionTypes().length === 0}
        />
        
        {getAvailableConditionTypes().length === 0 && conditions.length > 0 && (
          <div className="mt-2">
            <small className="text-gray-500">
              <i className="pi pi-info-circle mr-1"></i>
              Maximum conditions reached. You can only combine employee count with one other condition type.
            </small>
          </div>
        )}
      </Card>
    );
  };

  const renderTriggerConfiguration = () => (
    <Card title="Trigger Configuration" className="mb-4">
      <div className="formgrid grid">
        <div className="field col-12">
          <label className="block mb-2 font-semibold">
            Trigger Type <span className="text-red-500">*</span>
          </label>
          <div className="flex flex-column gap-2">
            {triggerTypeOptions.map((option) => (
              <div key={option.value} className="flex align-items-center">
                <input
                  type="radio"
                  id={`trigger-${option.value}`}
                  name="trigger_type"
                  value={option.value}
                  checked={formData.trigger_type === option.value}
                  onChange={(e) => handleInputChange('trigger_type', e.target.value)}
                  className="mr-2"
                />
                <label htmlFor={`trigger-${option.value}`} className="cursor-pointer">
                  {option.label}
                </label>
              </div>
            ))}
          </div>
        </div>
      </div>
      
  {formData.trigger_type === 'dsti' && (
        <div className="mt-4">
          <div className="p-3 bg-blue-50 border-l-4 border-blue-500 mb-4">
            <p className="text-blue-800">
      <strong>DTP Mode:</strong> This checklist will be triggered by Daily Task Plans. 
      The conditional requirements defined in Step 3 will determine when this checklist is applicable.
            </p>
          </div>
          
          {conditions.length === 0 && (
            <div className="p-3 bg-yellow-50 border-l-4 border-yellow-500">
              <p className="text-yellow-800">
                <strong>Note:</strong> No conditional requirements have been defined. 
                Please go back to Step 3 to configure the conditions that will trigger this checklist.
              </p>
            </div>
          )}
          
          {conditions.length > 0 && (
            <div className="p-3 bg-green-50 border-l-4 border-green-500">
              <p className="text-green-800">
                <strong>Configured Conditions:</strong> This checklist will be triggered when the following conditions are met:
              </p>
              <ul className="mt-2 ml-4">
                {conditions.map((condition, index) => (
                  <li key={index} className="text-green-700">
                    {conditionTypeOptions.find(opt => opt.value === condition.condition_type)?.label}
                    {condition.condition_type === 'employee_count' && 
                      ` (more than ${condition.threshold_value} employees)`
                    }
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}
      
      {formData.trigger_type === 'frequency' && (
        <div className="mt-4">
          <div className="formgrid grid">
            <div className="field col-12">
              <label htmlFor="frequency" className="block mb-2 font-semibold">
                Frequency <span className="text-red-500">*</span>
              </label>
              <div className="flex align-items-center gap-2">
                <span>Every</span>
                <InputNumber
                  value={formData.frequency_interval_days}
                  onValueChange={(e) => handleInputChange('frequency_interval_days', e.value)}
                  min={1}
                  className="w-4rem"
                />
                <Dropdown
                  value={formData.frequency_type}
                  onChange={(e) => handleInputChange('frequency_type', e.value)}
                  options={frequencyOptions}
                  className="flex-1"
                />
              </div>
            </div>
          </div>
        </div>
      )}
    </Card>
  );

  const renderCategoriesQuestions = () => (
    <div>
      <div className="mb-4">
        <h3>Categories and Questions</h3>
      </div>
      
      {categories.map((category, categoryIndex) => (
        <Card key={categoryIndex} className="mb-4">
          <div className="flex justify-content-between align-items-start mb-3">
            <div className="flex-1 mr-3">
              <div className="formgrid grid">
                <div className="field col-12 md:col-8">
                  <label className="block mb-2 font-semibold">
                    Category Name <span className="text-red-500">*</span>
                  </label>
                  <InputText
                    value={category.name || ''}
                    onChange={(e) => updateCategory(categoryIndex, 'name', e.target.value)}
                    placeholder="General Safety Inspection"
                    className="w-full"
                    invalid={!category.name || !(category.name || '').trim()}
                  />
                </div>
                <div className="field col-12">
                  <QuillEditor
                    label="Description"
                    content={category.description || ''}
                    setContent={(value: string) => updateCategory(categoryIndex, 'description', value)}
                    heightPx={15}
                    toast={toast}
                    hidePlaceholder={true}
                  />
                </div>
              </div>
            </div>
            
            <div className="flex gap-2">
              <Button
                icon="pi pi-arrow-up"
                className="p-button-outlined p-button-sm"
                onClick={() => moveCategoryUp(categoryIndex)}
                disabled={categoryIndex === 0}
                tooltip="Move up"
              />
              <Button
                icon="pi pi-arrow-down"
                className="p-button-outlined p-button-sm"
                onClick={() => moveCategoryDown(categoryIndex)}
                disabled={categoryIndex === categories.length - 1}
                tooltip="Move down"
              />
              <Button
                icon="pi pi-trash"
                className="p-button-danger p-button-outlined p-button-sm"
                onClick={() => removeCategory(categoryIndex)}
                tooltip="Delete category"
              />
            </div>
          </div>
          
          <Divider />
          {/* Photo Requirements fixed field */}
          {(() => {
            const photoIndex = category.questions.findIndex(q => q.question_text === 'Photo Requirements');
            if (photoIndex === -1) {
              // Inject if missing
              const updated = [...categories];
              updated[categoryIndex].questions.unshift({
                question_text: 'Photo Requirements',
                question_instruction: '',
                order_index: 0,
                required: false,
                non_compliant_options: []
              });
              setCategories(updated);
              return null;
            }
            const pq = category.questions[photoIndex];
            return (
              <Card key={`photo-${categoryIndex}`} className="mb-3 surface-100">
                <div className="formgrid grid">
                  <div className="field col-12">
                    <div className="flex justify-content-between align-items-center mb-2">
                      <label className="font-semibold">Photo Requirements</label>
                      <div className="flex align-items-center gap-2">
                        <label className="font-semibold">Required</label>
                        <InputSwitch
                          checked={pq.required || false}
                          onChange={(e) => {
                            const isRequired = (e as any).value as boolean;
                            updateQuestion(categoryIndex, photoIndex, 'required', isRequired);
                          }}
                        />
                      </div>
                    </div>
                    <InputTextarea
                      value={pq.question_instruction || ''}
                      onChange={(e) => updateQuestion(categoryIndex, photoIndex, 'question_instruction', e.target.value)}
                      placeholder="Describe how the photo should be taken (e.g., subjects, angles, clarity, hazards to show)"
                      rows={3}
                      className="w-full"
                    />
                  </div>
                </div>
              </Card>
            );
          })()}
          
          <div className="mb-3">
            <h5>Questions</h5>
          </div>
          
          {category.questions.filter(q => q.question_text !== 'Photo Requirements').length === 0 && (
            <div className="p-3 bg-gray-50 border-1 border-gray-300 border-round mb-3 text-center">
              <p className="text-gray-600 m-0">
                <i className="pi pi-info-circle mr-2"></i>
                No questions added yet. Click "Add Question" below to create your first question.
              </p>
            </div>
          )}
          
          {category.questions.filter(q => q.question_text !== 'Photo Requirements').map((question, questionIndex) => (
            <Card key={questionIndex} className="mb-3 surface-100">
              <div className="flex justify-content-between align-items-start mb-3">
                <div className="flex-1 mr-3">
                  <div className="formgrid grid">
                    <div className="field col-12">
                      <div className="mb-2">
                        <label className="font-semibold">Question Text</label>
                      </div>
                      <InputTextarea
                        value={question.question_text || ''}
                        onChange={(e) => {
                          // Need to find the actual index in the original questions array
                          const actualQuestionIndex = category.questions.findIndex(q => q === question);
                          updateQuestion(categoryIndex, actualQuestionIndex, 'question_text', e.target.value);
                        }}
                        placeholder="Basic safety inspection requirements"
                        rows={2}
                        className="w-full"
                        invalid={!question.question_text || !(question.question_text || '').trim()}
                      />
                    </div>
                    
                    <div className="field col-12">
                      <QuillEditor
                        label="Instructions (Optional)"
                        content={question.question_instruction || ''}
                        setContent={(value: string) => {
                          // Need to find the actual index in the original questions array
                          const actualQuestionIndex = category.questions.findIndex(q => q === question);
                          updateQuestion(categoryIndex, actualQuestionIndex, 'question_instruction', value);
                        }}
                        heightPx={15}
                        toast={toast}
                        hidePlaceholder={true}
                      />
                    </div>
                  </div>
                </div>
                
                <div className="flex gap-2">
                  <Button
                    icon="pi pi-arrow-up"
                    className="p-button-outlined p-button-sm"
                    onClick={() => {
                      const actualQuestionIndex = category.questions.findIndex(q => q === question);
                      moveQuestionUp(categoryIndex, actualQuestionIndex);
                    }}
                    disabled={(() => {
                      const actualQuestionIndex = category.questions.findIndex(q => q === question);
                      return actualQuestionIndex <= 1; // Can't move above Photo Requirements (index 0)
                    })()}
                    tooltip="Move up"
                  />
                  <Button
                    icon="pi pi-arrow-down"
                    className="p-button-outlined p-button-sm"
                    onClick={() => {
                      const actualQuestionIndex = category.questions.findIndex(q => q === question);
                      moveQuestionDown(categoryIndex, actualQuestionIndex);
                    }}
                    disabled={(() => {
                      const actualQuestionIndex = category.questions.findIndex(q => q === question);
                      return actualQuestionIndex === category.questions.length - 1;
                    })()}
                    tooltip="Move down"
                  />
                  <Button
                    icon="pi pi-trash"
                    className="p-button-danger p-button-outlined p-button-sm"
                    onClick={() => {
                      const actualQuestionIndex = category.questions.findIndex(q => q === question);
                      removeQuestion(categoryIndex, actualQuestionIndex);
                    }}
                    tooltip="Delete question"
                  />
                </div>
              </div>
              
              <div className="flex justify-content-between align-items-center mb-2">
                <h6>Non-Compliant Options</h6>
                <Button
                  label="Add Option"
                  icon="pi pi-plus"
                  size="small"
                  onClick={() => {
                    const actualQuestionIndex = category.questions.findIndex(q => q === question);
                    addNonCompliantOption(categoryIndex, actualQuestionIndex);
                  }}
                  className="p-button-outlined p-button-secondary"
                />
              </div>
              
              {question.non_compliant_options.map((option, optionIndex) => (
                <div key={optionIndex} className="border-1 border-300 p-3 mb-2 border-round">
                  <div className="flex align-items-center gap-2 mb-2">
                    <InputText
                      value={option.option_text || ''}
                      onChange={(e) => {
                        const actualQuestionIndex = category.questions.findIndex(q => q === question);
                        updateNonCompliantOption(categoryIndex, actualQuestionIndex, optionIndex, 'option_text', e.target.value);
                      }}
                      placeholder="Equipment not properly secured"
                      className="flex-1"
                    />
                    <Button
                      icon="pi pi-trash"
                      className="p-button-danger p-button-outlined p-button-sm"
                      onClick={() => {
                        const actualQuestionIndex = category.questions.findIndex(q => q === question);
                        removeNonCompliantOption(categoryIndex, actualQuestionIndex, optionIndex);
                      }}
                    />
                  </div>
                  
                  <div className="flex justify-content-between align-items-center mb-2">
                    <label className="font-semibold">Corrective Actions</label>
                    <Button
                      label="Add Action"
                      icon="pi pi-plus"
                      size="small"
                      onClick={() => {
                        const actualQuestionIndex = category.questions.findIndex(q => q === question);
                        addCorrectiveAction(categoryIndex, actualQuestionIndex, optionIndex);
                      }}
                      className="p-button-outlined p-button-secondary"
                    />
                  </div>
                  
                  {option.corrective_actions.map((action, actionIndex) => (
                    <div key={actionIndex} className="flex align-items-center gap-2 mb-1">
                      <InputText
                        value={action.action_text || ''}
                        onChange={(e) => {
                          const actualQuestionIndex = category.questions.findIndex(q => q === question);
                          updateCorrectiveAction(categoryIndex, actualQuestionIndex, optionIndex, actionIndex, e.target.value);
                        }}
                        placeholder="Secure equipment before proceeding"
                        className="flex-1"
                      />
                      <Button
                        icon="pi pi-trash"
                        className="p-button-danger p-button-outlined p-button-sm"
                        onClick={() => {
                          const actualQuestionIndex = category.questions.findIndex(q => q === question);
                          removeCorrectiveAction(categoryIndex, actualQuestionIndex, optionIndex, actionIndex);
                        }}
                      />
                    </div>
                  ))}
                </div>
              ))}
            </Card>
          ))}
          
          <div className="mt-3">
            <Button
              label="Add Question"
              icon="pi pi-plus"
              size="small"
              onClick={() => addQuestion(categoryIndex)}
              className="p-button-outlined"
            />
          </div>
        </Card>
      ))}
      
      <div className="mt-3">
        <Button
          label="Add Category"
          icon="pi pi-plus"
          onClick={addCategory}
          className="p-button-outlined"
        />
      </div>
    </div>
  );

  const renderCurrentStep = () => {
    switch (currentStep) {
      case 1:
        return renderBasicInformation();
      case 2:
        return renderIndustryAppointment();
      case 3:
        return renderConditionalRequirements();
      case 4:
        return renderTriggerConfiguration();
      case 5:
        return renderCategoriesQuestions();
      default:
        return renderBasicInformation();
    }
  };

  const dialogFooter = (
    <div className="flex justify-content-between pt-3" style={{ borderTop: '1px solid #e0e0e0' }}>
      <div>
        <Button
          label="Cancel"
          icon="pi pi-times"
          onClick={handleCancel}
          className="p-button-outlined p-button-secondary"
        />
      </div>
      
      <div className="flex gap-2">
        {currentStep > 1 && (
          <Button
            label="Previous"
            icon="pi pi-arrow-left"
            onClick={prevStep}
            className="p-button-outlined"
          />
        )}
        
        {(shouldRestrictFields && currentStep < 4) || (!shouldRestrictFields && currentStep < 5) ? (
          <Button
            label="Next"
            icon="pi pi-arrow-right"
            iconPos="right"
            onClick={nextStep}
          />
        ) : (
          <Button
            label={template ? "Update Checklist" : "Save Checklist"}
            icon="pi pi-check"
            onClick={handleSave}
            loading={loading}
            className="p-button-success"
          />
        )}
      </div>
    </div>
  );

  const dialogHeader = (
    <div>
      <h2 className="m-0 mb-2">{template ? 'Edit Checklist' : 'Create New Checklist'}</h2>
      <p className="m-0 text-gray-600">Configure a new safety checklist template</p>
    </div>
  );

  return (
    <>
      {/* Toast: append to body and elevate z-index so it is not hidden behind dialogs */}
      <Toast
        ref={toast}
        appendTo={typeof document !== 'undefined' ? document.body : undefined}
        style={{ zIndex: 2000 }}
      />
      <ConfirmDialog />
      
      <Dialog
        visible={visible}
        onHide={onHide}
        header={dialogHeader}
        footer={dialogFooter}
        style={{ width: '90vw', maxWidth: '1200px', height: '90vh' }}
        className="p-fluid"
        maximizable
        modal
      >
        <div className="h-full overflow-auto">
          {renderStepIndicator()}
          {renderCurrentStep()}
        </div>
      </Dialog>
    </>
  );
}
